"use client";

import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import {
	signInWithEmail,
	signInWithPhone,
	verifyEmailOtp,
	verifySmsOtp,
} from "@/lib/auth/auth";
import { cn } from "@/lib/utils";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { UserIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../ui/card";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";

interface VerifyOtpFormParams {
	type: "email" | "phone";
	email?: string;
	phone?: string;
	className?: string;
}

export default function VerifyOtpForm(props: VerifyOtpFormParams) {
	const expirationTime = 60;
	let id: NodeJS.Timeout | null = null;

	const [timeLeft, setTimeLeft] = useState(expirationTime);
	const [otp, setOtp] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (timeLeft > 0) {
			id = setTimeout(() => {
				setTimeLeft(timeLeft - 1);
			}, 1000);
		}
		return () => {
			if (!id) return;
			clearTimeout(id);
		};
	}, [timeLeft, id]);

	const verify = (otp: string) => {
		if (props.type === "email") {
			if (!props.email) throw new Error("Email is required");
			return verifyEmailOtp({ email: props.email, otp });
		}

		if (!props.phone) throw new Error("Phone is required");
		return verifySmsOtp({ phone: props.phone, otp });
	};

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setError(null);

		try {
			const { success, session, error } = await verify(otp);
			if (error) throw ErrorEvent;
			toast("User successfully logged in", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});
		} catch (error: unknown) {
			setError(error instanceof Error ? error.message : "An error occurred");
			toast("Error logging in. Please try again", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	const resend = () => {
		if (props.type === "email") {
			if (!props.email) throw new Error("Email is required");
			return signInWithEmail(props.email);
		}
		if (!props.phone) throw new Error("Phone is required");
		return signInWithPhone(props.phone);
	};

	const handleResend = async () => {
		setIsLoading(true);
		setError(null);
		try {
			const { success, error } = await resend();
			if (error) throw ErrorEvent;
			toast("OTP sent successfully", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});
		} catch (error: unknown) {
			setError(error instanceof Error ? error.message : "An error occurred");
			toast("Error sending OTP. Please try again", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
		} finally {
			setTimeLeft(expirationTime);
			setIsLoading(false);
		}
	};

	return (
		<Card
			className={cn("bg-transparent border-none shadow-none", props.className)}
		>
			<CardHeader className="items-center justify-center text-center space-y-4">
				<ZeekMapsIcon className="w-16 h-16" />
				<CardTitle className="text-3xl font-extrabold tracking-tighter">
					{props.type === "email" ? "Email Verification" : "Phone Verification"}
				</CardTitle>
				<CardDescription>
					Please verify your {props.type === "email" ? "email" : "phone number"}
					.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<form onSubmit={handleVerify} className="space-y-3">
					<Field className="w-full py-3 items-center justify-center mx-auto">
						<FieldLabel
							htmlFor="phone"
							className="block text-md font-medium text-primary/50 text-center"
						>
							One Time Password sent to{" "}
							<span className="font-semibold text-primary">
								{props.type === "email" ? props.email : props.phone}
							</span>
							.
						</FieldLabel>

						<InputOTPComponent onComplete={(otp) => setOtp(otp)} className="" />
					</Field>

					<div className="text-center text-xs">
						{error && <p className="text-xs text-red-500">{error}</p>}
						<p className="text-primary/80 text-xs text-center font-bold">
							{timeLeft > 0
								? `OTP expires in ${timeLeft} seconds`
								: "OTP expired. Please try again."}
							{timeLeft === 0 && (
								<Button
									type="button"
									variant="link"
									className="font-semibold text-xs text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
									onClick={handleResend}
									disabled={timeLeft > 0}
								>
									Resend OTP
								</Button>
							)}
						</p>
					</div>
				</form>
			</CardContent>

			<CardFooter>
				<Button
					type="submit"
					variant="default"
					disabled={isLoading}
					form="form-verify-otp"
					className="w-full rounded-full"
				>
					{isLoading ? "Verifying..." : "Verify"}
				</Button>
			</CardFooter>
		</Card>
	);
}

interface InputOTPComponentProps {
	onComplete: (otp: string) => void;
	className?: string;
}

export function InputOTPComponent({
	onComplete,
	className,
}: InputOTPComponentProps) {
	return (
		<InputOTP
			maxLength={6}
			inputMode="numeric"
			placeholder="******"
			pattern={REGEXP_ONLY_DIGITS}
			onComplete={onComplete}
			className={cn("justify-center", className)}
		>
			<InputOTPGroup>
				<InputOTPSlot index={0} className="w-12 h-12" />
				<InputOTPSlot index={1} className="w-12 h-12" />
				<InputOTPSlot index={2} className="w-12 h-12" />
			</InputOTPGroup>
			<InputOTPSeparator />
			<InputOTPGroup>
				<InputOTPSlot index={3} className="w-12 h-12" />
				<InputOTPSlot index={4} className="w-12 h-12" />
				<InputOTPSlot index={5} className="w-12 h-12" />
			</InputOTPGroup>
		</InputOTP>
	);
}
